/* eslint-disable react/no-multi-comp */

import { getClosest, getParents } from 'util/dom'
import AgentLabel from './AgentLabel'
import GroupLabel from './GroupLabel'
import MailboxLabel from './MailboxLabel'
import integrationAction from './IntegrationAction'
import AgentAvatar from './AgentAvatar'
import ProviderIcon from './ProviderIcon'
import IntegrationExternalAction from './IntegrationExternalAction'

export const handlePrintClick = e => {
  const startingNode = getClosest(e.target, '[class*="message"]')
  if (!startingNode) return
  const parents = getParents(startingNode)
  const allChildren = Array.from(startingNode.querySelectorAll('*'))
  const nodes = parents
    .concat(allChildren)
    .filter(
      node =>
        !node.classList.contains('hidden-in-print') &&
        !node.classList.contains('visible-only-in-print')
    )

  nodes.forEach(node => {
    let display
    if (node.className.match('meta')) {
      display = 'flex'
    } else {
      const computedStyle = getComputedStyle(node)
      display = computedStyle.display
    }
    node.style.setProperty('display', display, 'important')
  })

  const style = document.createElement('style')
  style.appendChild(document.createTextNode('')) // webkit hack
  document.head.appendChild(style)
  style.sheet.insertRule(
    '@media print { :not(.visible-only-in-print) { display: none; } }'
  )
  style.sheet.insertRule(
    '@media print { .message_actions { opacity: 0 !important; } }'
  )
  style.sheet.insertRule(
    '@media print { .changeset .action .contents .header .right-side .meta { display: block !important; } }'
  )

  function cleanup() {
    style.parentNode.removeChild(style)
    startingNode.style.display = ''
    nodes.forEach(node => {
      if (node.className.match('meta')) {
        node.style.removeProperty('display')
      }
    })
    window.removeEventListener('afterprint', cleanup)
  }
  window.addEventListener('afterprint', cleanup)
  window.print()
}

export const shouldStartExpanded = (
  isForward,
  isFirstMessage,
  parsedBody,
  index
) => {
  // If the following part is text then we don't want to collapse
  // the quoted part. This is a way to try to stop us collapsing
  // quoted text in the middle of message bodies, which was
  // originally intended functionality.
  // Now we are only collapsing quoted parts after the final text
  // part, which should just be the quoted replies.
  //
  const nextPart = parsedBody[index + 1]
  const nextPartText = nextPart && nextPart.type === 'text'

  // If this is an enduser forward and it's the first message in
  // a ticket then we need to expand the quoted part, because that
  // is the forwarded message. This is a bit of a hack but I can't
  // think of a better way right now.
  const firstMessageAndEnduserForward = isForward && isFirstMessage

  return nextPartText || firstMessageAndEnduserForward
}

const previousUserText = customer => {
  if (!customer) return ''
  const { name, email } = customer
  if (name && email) return ` from ${name} (${email})`
  if (name) return ` from ${name}`
  if (email) return ` from ${email}`
  return ''
}

export const getAuthor = (action, currentUserId) => {
  const actor = action.actor
  let author = actor.label || actor.name
  if (actor.type === 'Agent' && actor.id === currentUserId) {
    author = 'You'
  } else if (actor.type === 'Rule') {
    author = 'Rule'
  } else if (actor.type === 'Agent') {
    author = <AgentLabel agentId={actor.id} />
  }
  return author
}

export const getByline = ({
  action,
  currentUserId,
  isNote,
  isFirstMessage,
  isForward,
}) => {
  const author = getAuthor(action, currentUserId)

  let operation = null
  let icon = null

  const type = action.change_type
  const change = action.change

  switch (type) {
    case 'Message':
      if (isNote) {
        operation = <span className="operation note">added a note</span>
      } else if (!isNote && isFirstMessage) {
        operation = (
          <span className="operation message">started a conversation</span>
        )
      } else {
        try {
          const recipient = change.to[0].name || change.to[0].email
          const actionText = isForward ? 'forwarded' : 'replied'

          operation = (
            <span className="operation message">
              {actionText} to {recipient}
            </span>
          )
        } catch (e) {} // eslint-disable-line no-empty
      }
      break
    case 'State':
      operation = [
        'marked as',
        action.change.state === 'closed' ? 'closed' : 'open',
      ].join(' ')
      break
    case 'Star':
      operation = change.starred ? 'added star' : 'removed star'
      break
    case 'Agent':
    case 'Group':
    case 'AgentAndGroup':
      if (!change.id) {
        operation = 'unassigned'
      } else if (change.id === action.actor.id) {
        operation = 'took ownership'
      } else if (type === 'Group') {
        operation = (
          <React.Fragment>
            assigned to <GroupLabel groupId={action.change.id} />
          </React.Fragment>
        )
      } else {
        operation = (
          <React.Fragment>
            assigned to <AgentLabel agentId={action.change.id} />
          </React.Fragment>
        )
      }
      break
    case 'Mailbox':
      operation = (
        <React.Fragment>
          moved to <MailboxLabel mailboxId={action.change.mailbox} />
        </React.Fragment>
      )
      break
    case 'Snooze::State':
      operation = 'snoozed'
      break
    case 'Unsnooze::State':
      operation = 'unsnoozed'
      break
    case 'Ticket::Merger':
      operation = 'merged'
      break
    case 'Title':
      if (change.previousTitle) {
        operation = `changed title from "${change.previousTitle}"`
      } else if (change.title) {
        operation = `changed title to "${change.title}"`
      } else {
        operation = 'removed title'
      }
      break
    case 'Deleted':
      operation = change.restored ? 'restored' : 'deleted'
      break
    case 'Ticket::Integration':
      icon = <ProviderIcon provider={change.provider} />
      operation = integrationAction(action)
      break
    case 'IntegrationExternal':
      icon = <ProviderIcon provider={change.provider} />
      operation = <IntegrationExternalAction action={action} />
      break
    case 'Enduser':
      operation = `changed user${previousUserText(
        action.change.previousCustomer
      )}`
      break
    default:
      operation = type
  }

  return (
    <div className="byline">
      {icon} <span className="author">{author}</span> {operation}
    </div>
  )
}

export const getAvatarComponent = ({ action }) => {
  const actor = action.actor
  const avatarUrl = actor.avatarUrl

  // if possible, use the avatar provided, otherwise, use the store
  if (actor.type === 'Agent' && !avatarUrl) {
    return <AgentAvatar agentId={actor.id} />
  }
  const actorInitials = actor.initials
  const styles = { backgroundImage: `url(${avatarUrl})` }
  return (
    <div className="avatar" style={styles}>
      {!avatarUrl && actorInitials}
    </div>
  )
}
